import { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import { getMe, logout as apiLogout, processSession } from "./api";

/**
 * CODA auth context.
 *
 * Flow:
 *  1. Login button → redirects to the OAuth provider's hosted login page
 *     (REACT_APP_OAUTH_LOGIN_URL). The redirect URL is derived dynamically
 *     from window.location.origin — never hardcoded, never fallbacked.
 *     REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS,
 *     THIS BREAKS THE AUTH.
 *  2. Provider returns to {origin}/auth/callback#session_id=...
 *  3. AppRouter detects the fragment SYNCHRONOUSLY during render and mounts
 *     <AuthCallback>, which posts the id to our backend and lands the user.
 *  4. Provider exposes { user, loading, signIn, signOut, refresh } to consumers.
 */

const OAUTH_LOGIN_URL =
    process.env.REACT_APP_OAUTH_LOGIN_URL || "https://auth.emergentagent.com/";

// Waits between /me retries after a non-401 failure (~30 s in total). After
// the last one we stop retrying and stop loading with whatever user we had.
export const AUTH_RETRY_DELAYS_MS = [1000, 2000, 4000, 8000, 15000];

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

const AuthContext = createContext({
    user: null,
    loading: true,
    signIn: () => {},
    signOut: () => {},
    refresh: async () => {},
    setUser: () => {},
});

export function AuthProvider({ children }) {
    const [user, setUser] = useState(null);
    // `null` = unknown / checking. We *don't* start a /me check if the URL has
    // a session_id fragment — AuthCallback will exchange it first and call
    // setUser() directly, avoiding the global-provider-vs-callback race.
    const [loading, setLoading] = useState(
        () => !(typeof window !== "undefined" && window.location.hash?.includes("session_id="))
    );

    const mounted = useRef(true);
    useEffect(() => {
        mounted.current = true;
        return () => { mounted.current = false; };
    }, []);

    // Only an explicit 401 (getMe → null) signs the user out. A 5xx, a
    // gateway timeout or a network error says nothing about the session, so
    // we keep the user we have and retry; `loading` stays true meanwhile so
    // pages don't render (or fetch) as anonymous. Dropping the user on any
    // error made a busy backend (e.g. while a branch computes) look like a
    // sign-out, and the analyses list then refetched without the session.
    const refresh = useCallback(async () => {
        for (let attempt = 0; ; attempt++) {
            try {
                const me = await getMe();
                if (mounted.current) setUser(me);
                break;
            } catch (_) {
                if (attempt >= AUTH_RETRY_DELAYS_MS.length || !mounted.current) break;
                await sleep(AUTH_RETRY_DELAYS_MS[attempt]);
            }
        }
        if (mounted.current) setLoading(false);
    }, []);

    useEffect(() => {
        // CRITICAL: skip /me check during the OAuth return — AuthCallback
        // handles authentication in that case and calls setUser directly.
        if (typeof window !== "undefined" && window.location.hash?.includes("session_id=")) {
            return;
        }
        refresh();
    }, [refresh]);

    const signIn = useCallback(() => {
        // Derive redirect dynamically — DO NOT HARDCODE OR FALLBACK.
        // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT
        // URLS, THIS BREAKS THE AUTH.
        const target =
            (typeof window !== "undefined" && sessionStorage.getItem("scope:post_login")) ||
            "/";
        const redirectUrl = window.location.origin + "/auth/callback?next=" + encodeURIComponent(target);
        window.location.href =
            OAUTH_LOGIN_URL + "?redirect=" + encodeURIComponent(redirectUrl);
    }, []);

    const signOut = useCallback(async () => {
        try {
            await apiLogout();
        } catch (_) {
            // ignore — we still drop client state
        }
        setUser(null);
    }, []);

    const value = { user, loading, signIn, signOut, refresh, setUser };
    return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
    return useContext(AuthContext);
}

export { AuthContext, processSession };
